import { type ComponentInternalInstance, getCurrentInstance, onBeforeUnmount, onMounted, onUnmounted } from 'vue';

import { GlobalState } from '@/global';

import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { useIdleTimeout } from '../helpers/idle.helpers';
import { Auth } from './auth.service';
import { Logger } from './logger';
import { SSEService } from './sse';

const IdleTimeoutMs = import.meta.env.VITE_APP_AUTH_TIMEOUT_MINS * 60 * 1000;
const InstallIdleTimeoutMs = import.meta.env.VITE_APP_AUTH_TIMEOUT_MINS_INSTALL * 60 * 1000;
const IdleBlockingInstances = new Set<ComponentInternalInstance>();

const logger = Logger.createScoped('SessionManagement');

const SSEAuthPermissionsChannel = SSEService.createEventChannel('auth.permissions');
const SSEAuthLogoutChannel = SSEService.createEventChannel('auth.logout');

export class SessionManagement {
    static install() {
        this.useJwtRenewal();
        this.useSessionIdleTimeout();

        SSEAuthPermissionsChannel.subscribe(() => Auth.reloadPermissions(false));
        SSEAuthLogoutChannel.subscribe(() => Auth.clearStoredJwt());
    }

    static useJwtRenewal() {
        let jwtRefreshInterval: number | null = null;

        const checkJwtRefresh = () => {
            const jwtExp = Auth.activeJwtExp;
            if (!jwtExp) return;

            const renewTs = jwtExp - 300_000; /* 5 minutes */
            if (Date.now() > renewTs) {
                Auth.renewJwt();
            }
        };

        function scheduleJwtRefresh() {
            jwtRefreshInterval = window.setInterval(checkJwtRefresh, 30_000);
            checkJwtRefresh();
        }

        function clearScheduledJwtRefresh() {
            if (jwtRefreshInterval) clearInterval(jwtRefreshInterval);
        }

        onMounted(scheduleJwtRefresh);
        onUnmounted(clearScheduledJwtRefresh);
    }

    static useSessionIdleTimeout() {
        if (MobileBridge.isWithinZynoSuiteMobile) return;
        useIdleTimeout({
            timeoutMs: 30_000, // this will fire more frequently than the timeout, so we dynamically configure our timeout
            onIdle: (idleMs: number) => {
                const idleTimeout = GlobalState.isInstall ? InstallIdleTimeoutMs : IdleTimeoutMs;
                if (idleMs < idleTimeout) return;
                if (!GlobalState.user.value) return;
                if (IdleBlockingInstances.size > 0) return;
                if (Auth.rememberMeExpiresAt.value && Auth.rememberMeExpiresAt.value > Date.now()) return;
                this.performTimeoutRedirect();
            }
        });
        window.addEventListener('focus', () => this.checkSession());
    }

    static checkSession() {
        if (!GlobalState.user.value) return;
        const jwtTimeout = Auth.activeJwtExp;
        if (!jwtTimeout || jwtTimeout < Date.now()) this.performTimeoutRedirect();
    }

    static performTimeoutRedirect() {
        logger.info('Session timed out');
        Auth.clearStoredJwt('Your session timed out due to inactivity.');
    }

    static useSessionTimeoutDisabler() {
        const instance = getCurrentInstance();
        if (!instance) return;

        onMounted(() => {
            IdleBlockingInstances.add(instance);
        });
        onBeforeUnmount(() => {
            IdleBlockingInstances.delete(instance);
        });
    }
}
